import { useAppDispatch, useAppSelector } from '@/app/reduxHooks'
import { closeOverlayAsync } from '@/features/adp/state/overlay.thunks'
import type { ColumnMappingOverlayData } from '@/features/adp/state/overlaySlice'
import { useSourceData } from '@/features/adp/hooks/useSourceData'
import { useTransformationData } from '@/features/adp/hooks/useTransformationData'
import { useIntegrationData } from '@/features/adp/hooks/useIntegrationData'
import { useTargetData } from '@/features/adp/hooks/useTargetData'
import {
  useDataFlowMappingMutation,
  useUpdateDataFlowMutation,
} from '@/features/adp/mutations/usePipelineMutations'
import { Button } from '@/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/select'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/dialog'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  ChevronLeft,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeft,
  ChevronsRight,
  Trash2,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import styles from './ColumnMapping.module.scss'
import { cn } from '@/shared/lib/utils'
import {
  buildDataFlowMappingPayload,
  buildColumnMappingPayload,
  buildColumnMappingRowsWithProjection,
  buildJoinSecondaryColumnAllowlist,
  buildUnionSecondarySourceNames,
  computeSourceFieldsSignature,
  getMissingMappingPayloadFields,
  isTargetColumn,
  normalizeSourceSessionsForColumnMapping,
  resolveDestinationDataType,
  type ColumnMappingRow,
} from './columnMapping.utils'
import { buildTransformationDefinition } from '../../../../utils/transformationDefinition.utils'
import { store } from '@/app/store'
import { toast } from 'sonner'
import { setRunEnabled } from '@/features/adp/state/pipelineSlice'
import {
  extractRenameOverrides,
  extractDropFieldsColumns,
  extractTransposeIgnoreColumns,
  extractAggregationReplayProjection,
  resolveCreatedFilesPayload,
} from '@/features/adp/components/destination/dialogs/output-preview/outputPreview.utils'
import { getUpstreamNodeIds } from '@/features/adp/components/canvas/canvas.utils'

const columnHelper = createColumnHelper<ColumnMappingRow>()

const ColumnMapping = () => {
  const { type, isOpen } = useAppSelector(state => state.overlay)
  const columnMappingOverlayData = useAppSelector(state =>
    state.overlay.type === 'column-mapping' && state.overlay.isOpen
      ? (state.overlay.data as ColumnMappingOverlayData)
      : null
  )
  const isOpenForColumnMapping = isOpen && type === 'column-mapping'
  const dispatch = useAppDispatch()
  /** Source fields come from source nodes; overlay `nodeId` is the destination node — use all source sessions. */
  const { getAllSourceData, getSourceDataByNodeId } = useSourceData()
  const { getTransformationDataByNodeId } = useTransformationData()
  const { getIntegrationDataByNodeId } = useIntegrationData()
  const { getTargetDataByNodeId, updateTargetData } = useTargetData()
  const updateDataFlowMutation = useUpdateDataFlowMutation()
  const dataFlowMappingMutation = useDataFlowMappingMutation()
  const [rows, setRows] = useState<ColumnMappingRow[]>([])
  const [pageSize, setPageSize] = useState(5)

  const destinationNodeId = columnMappingOverlayData?.nodeId ?? null

  /** String signatures avoid unstable array deps while still reacting to store content changes. */
  const sourceSignature = computeSourceFieldsSignature(
    normalizeSourceSessionsForColumnMapping(getAllSourceData())
  )

  const targetData = destinationNodeId ? getTargetDataByNodeId(destinationNodeId)?.data : null
  const targetColumns = Array.isArray(targetData?.target_columns)
    ? targetData.target_columns.filter(isTargetColumn)
    : []

  const targetSignature = targetColumns
    .map(column => `${column.column_name}:${column.column_data_type}`)
    .join('|')

  useEffect(() => {
    if (!isOpenForColumnMapping) return

    const nextTargetData = destinationNodeId ? getTargetDataByNodeId(destinationNodeId)?.data : null
    const nextTargetColumns = Array.isArray(nextTargetData?.target_columns)
      ? nextTargetData.target_columns.filter(isTargetColumn)
      : []
    const { nodes, edges } = store.getState().canvas.present
    const upstreamIds = destinationNodeId ? getUpstreamNodeIds(destinationNodeId, edges) : []
    const nodeMap = new Map(nodes.map(n => [n.id, n]))
    const upstreamTransformationIds = upstreamIds.filter(
      id => nodeMap.get(id)?.type === 'transformation'
    )
    const upstreamIntegrationIds = upstreamIds.filter(
      id => nodeMap.get(id)?.type === 'integration'
    )
    /** Limits each join-secondary source's fields to the columns the Join emits, so the
     * secondary's join-key (already present on the primary) doesn't duplicate a row. */
    const joinSecondaryAllowlist = buildJoinSecondaryColumnAllowlist(upstreamIntegrationIds)
    /** Drops every column from each union-secondary source. Union output has primary's
     * schema, so any column from the secondary would duplicate a primary row. */
    const unionSecondarySources = buildUnionSecondarySourceNames(upstreamIntegrationIds)
    const sources = normalizeSourceSessionsForColumnMapping(
      getAllSourceData(),
      joinSecondaryAllowlist,
      unionSecondarySources,
    )
    const renameOverrides = extractRenameOverrides(upstreamTransformationIds)
    const dropFields = extractDropFieldsColumns(upstreamTransformationIds)
    const transposeIgnoreColumns = extractTransposeIgnoreColumns(upstreamTransformationIds)
    const rawFiles = resolveCreatedFilesPayload(upstreamIds, getSourceDataByNodeId)
    const aggregationProjection = extractAggregationReplayProjection(
      upstreamTransformationIds,
      rawFiles
    )
    const effectiveDropFields = [
      ...dropFields,
      ...transposeIgnoreColumns,
      ...(aggregationProjection?.dropFields ?? []),
    ]

    const nextRows = buildColumnMappingRowsWithProjection({
      sources,
      targetColumns: nextTargetColumns,
      renameOverrides,
      rawFiles,
      dropFields: effectiveDropFields,
      aggregationProjection,
    })

    setRows(nextRows)
  }, [
    isOpenForColumnMapping,
    destinationNodeId,
    sourceSignature,
    targetSignature,
    getAllSourceData,
    getSourceDataByNodeId,
    getTargetDataByNodeId,
  ])

  const destinationOptions = useMemo(
    () => targetColumns.map(column => column.column_name),
    [targetColumns]
  )

  const handleDestinationColumnChange = (idx: number, value: string) => {
    setRows(prevRows => {
      const nextRows = [...prevRows]
      const current = nextRows[idx]
      if (!current) {
        return prevRows
      }

      nextRows[idx] = {
        ...current,
        destinationName: value,
        destinationDataType: resolveDestinationDataType(value, targetColumns),
      }
      return nextRows
    })
  }

  const handleDelete = (idxToRemove: number) => {
    setRows(prevRows => prevRows.filter((_, idx) => idx !== idxToRemove))
  }

  const columns = [
    columnHelper.accessor('sourceName', {
      header: 'Source Name',
      size: 170,
      cell: info => <span className="font-normal text-[#2563eb]">{info.getValue()}</span>,
    }),
    columnHelper.accessor('sourceColumn', {
      header: 'Source Column',
      size: 180,
      cell: info => <span className="font-normal">{info.getValue()}</span>,
    }),
    columnHelper.accessor('sourceDataType', {
      header: 'Source Datatype',
      size: 150,
      cell: info => {
        const value = info.getValue().trim()
        if (!value) return null
        return <span className={styles.chip}>{value}</span>
      },
    }),
    columnHelper.accessor('destinationName', {
      header: 'Destination Column',
      size: 200,
      cell: info => (
        <Select
          value={info.getValue()}
          onValueChange={value => handleDestinationColumnChange(info.row.index, value)}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select destination column" />
          </SelectTrigger>
          <SelectContent>
            {destinationOptions.map(option => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ),
    }),
    columnHelper.accessor('destinationDataType', {
      header: 'Destination Datatype',
      size: 180,
      cell: info => <span className={styles.chip}>{info.getValue()}</span>,
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Delete',
      size: 90,
      cell: info => (
        <div className="flex justify-center">
          <Button
            variant="ghost"
            onClick={() => handleDelete(info.row.index)}
            title="Delete row"
            className="h-auto p-0 hover:bg-transparent"
          >
            <Trash2 className="size-5 text-black" />
          </Button>
        </div>
      ),
    }),
  ]

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize,
      },
    },
    autoResetPageIndex: false,
  })

  const isSaving = updateDataFlowMutation.isPending || dataFlowMappingMutation.isPending

  const handleSave = async () => {
    if (isSaving) return

    const filteredRows = rows.filter(
      row => row.destinationName.trim() && row.destinationDataType.trim()
    )
    const columnMappingPayload = buildColumnMappingPayload(filteredRows)
    const sourceEntries = getAllSourceData()
    const defaultSourceTable =
      sourceEntries
        .map(entry =>
          typeof entry.data?.source_selected_table === 'string'
            ? entry.data.source_selected_table.trim()
            : ''
        )
        .find(table => table.length > 0) ?? ''

    /** Collect entries from upstream transformation AND integration nodes.
     * Transformations live under `transformationData`, integrations under
     * `integrationData`; both contribute to the JSON definition (e.g. JR for
     * Join). Omitting integrations drops Join blocks from the saved
     * `transformation_definition`. */
    const { nodes: canvasNodes, edges: canvasEdges } = store.getState().canvas.present

    const upstreamReplayNodeIds: string[] = []
    if (destinationNodeId) {
      const visited = new Set<string>()
      const queue: string[] = [destinationNodeId]

      while (queue.length > 0) {
        const currentId = queue.shift()!
        if (visited.has(currentId)) continue
        visited.add(currentId)

        const parentIds = canvasEdges.filter(e => e.target === currentId).map(e => e.source)

        for (const parentId of parentIds) {
          if (visited.has(parentId)) continue

          const parentNode = canvasNodes.find(n => n.id === parentId)
          if (!parentNode) continue

          if (parentNode.type === 'transformation' || parentNode.type === 'integration') {
            upstreamReplayNodeIds.push(parentId)
            queue.push(parentId) // Keep walking upstream through transformations/integrations.
          } else if (parentNode.type === 'source') {
            // Stop at source nodes — don't traverse further.
            visited.add(parentId)
          } else {
            queue.push(parentId)
          }
        }
      }

      // Reverse so steps are in pipeline order (source → … → destination).
      upstreamReplayNodeIds.reverse()
    }

    // Transformations persist under `transformationData`; integrations (Join,
    // Union) under `integrationData`. Pick the right getter per node so JR/UN
    // blocks make it into the emitted `transformation_definition`.
    const nodeTypeById = new Map(canvasNodes.map(n => [n.id, n.type]))
    const transformationEntries = upstreamReplayNodeIds
      .map(nodeId => {
        const entry = nodeTypeById.get(nodeId) === 'integration'
          ? getIntegrationDataByNodeId(nodeId)
          : getTransformationDataByNodeId(nodeId)
        return entry
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
      .map(entry => ({ data: entry.data as Record<string, unknown> }))

    const transformationDefinition = {
      ...buildTransformationDefinition({ transformationEntries, defaultSourceTable }),
      columnMapping: columnMappingPayload,
    }

    const dataFlowMappingPayload = buildDataFlowMappingPayload({
      sourceEntries: sourceEntries.map(entry => ({
        data: entry.data as Record<string, unknown>,
      })),
      targetData: (targetData as Record<string, unknown> | null | undefined) ?? null,
    })
    const missingFields = getMissingMappingPayloadFields(dataFlowMappingPayload)
    if (missingFields.length > 0) {
      console.warn('Cannot save column mapping: missing source/target mapping ids', {
        missingFields,
        payload: dataFlowMappingPayload,
      })
      return
    }

    try {
      const dataFlowId = store.getState().pipeline.dataFlowId
      if (!dataFlowId) {
        console.warn('Cannot save column mapping: missing dataFlowId')
        return
      }

      await updateDataFlowMutation.mutateAsync({
        dataFlowId: String(dataFlowId),
        payload: {
          transformation_definition: JSON.stringify(transformationDefinition),
        },
      })

      await dataFlowMappingMutation.mutateAsync({
        dataFlowId: String(dataFlowId),
        payload: dataFlowMappingPayload,
      })

      if (destinationNodeId) {
        updateTargetData(destinationNodeId, { columnMapping: columnMappingPayload })
      }

      toast.success('Destination configuration completed successfully')
      dispatch(closeOverlayAsync())
      dispatch(setRunEnabled(true))
    } catch (error) {
      console.error('Failed to save column mapping', error)
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      dispatch(closeOverlayAsync())
    }
  }

  return (
    <Dialog open={isOpenForColumnMapping} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader className="flex !flex-row items-center justify-start gap-2">
          <DialogClose asChild>
            <Button variant="ghost" size="icon">
              <ChevronLeft />
            </Button>
          </DialogClose>
          <DialogTitle className="!mt-0 text-xl font-medium">Column Mapping</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="w-full">
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full border-collapse">
                <thead className="border-b border-gray-200 bg-gray-50">
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th
                          key={header.id}
                          className="px-4 py-3 text-left text-sm font-semibold text-gray-900"
                          style={{ minWidth: header.getSize() }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {table.getRowModel().rows.map(row => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      {row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          className="px-4 py-3 text-sm text-gray-900"
                          style={{ minWidth: cell.column.getSize() }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-1 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Rows per page:</span>
                <Select
                  value={String(table.getState().pagination.pageSize)}
                  onValueChange={value => {
                    const nextSize = Number(value)
                    setPageSize(nextSize)
                    table.setPageSize(nextSize)
                  }}
                >
                  <SelectTrigger
                    className={cn(
                      'h-9 w-8 min-w-0 shrink-0 border-0 bg-transparent px-0 py-0 shadow-none',
                      'focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0',
                      'data-[state=open]:ring-0 data-[state=open]:ring-offset-0'
                    )}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 10, 20, 30, 40, 50].map(size => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-6">
                <span className="text-sm text-gray-700">
                  <span className="font-bold">
                    {rows.length === 0
                      ? 0
                      : table.getState().pagination.pageIndex *
                      table.getState().pagination.pageSize +
                      1}
                  </span>{' '}
                  to{' '}
                  <span className="font-bold">
                    {Math.min(
                      (table.getState().pagination.pageIndex + 1) *
                      table.getState().pagination.pageSize,
                      rows.length
                    )}
                  </span>{' '}
                  of <span className="font-bold">{rows.length}</span>
                </span>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => table.setPageIndex(0)}
                    disabled={!table.getCanPreviousPage()}
                    className="h-auto p-1"
                  >
                    <ChevronsLeft className="size-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    className="h-auto p-1"
                  >
                    <ChevronLeftIcon className="size-5" />
                  </Button>
                  <span className="px-2 text-sm text-gray-700">
                    Page{' '}
                    <span className="font-bold">{table.getState().pagination.pageIndex + 1}</span>{' '}
                    of <span className="font-bold">{table.getPageCount()}</span>
                  </span>
                  <Button
                    variant="ghost"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                    className="h-auto p-1"
                  >
                    <ChevronRightIcon className="size-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                    disabled={!table.getCanNextPage()}
                    className="h-auto p-1"
                  >
                    <ChevronsRight className="size-5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="!pr-0">
            <Button type="button" onClick={() => void handleSave()} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

export default ColumnMapping
